/**
 * Copyright(c) 2026 Magrabi.
 *
 * All rights reserved.
 *
 * This software is the confidential and proprietary information of Magrabi ("Confidential
 * Information"). You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the contract agreement you entered into with Magrabi.
 *
 * @module Algolia
 */

import type { RecommendationsRequest } from "@algolia/recommend";
import { recommendationType } from "../constants";
import { algoliaRecommendationParam } from "./constants";

export const RECOMMEND_THRESHOLD = 8;

export const RECOMMEND_VISIBILITY_FILTER =
  "isAvailableForPurchase:true AND defaultVariant.quantity > 0 AND defaultVariant.hasMedia:true";

export interface RecommendHitLike {
  objectID?: string;
  parentProductId?: string;
  defaultVariant?: { sku?: string };
  variantColorMappings?: Array<{ variantId?: string }>;
  queryID?: string;
  indexName?: string;
}

export interface RecommendRequestMeta {
  model: string;
  anchor?: string;
}

export const TRENDING_CATEGORY_FACET = "categories.en.lvl0";

export const buildRecommendRequests = (
  models: string[],
  anchorIds: string[],
  indexName: string,
  maxRecommendations: number,
  trendingCategory?: string,
): { requests: RecommendationsRequest[]; meta: RecommendRequestMeta[] } => {
  const requests: RecommendationsRequest[] = [];
  const meta: RecommendRequestMeta[] = [];
  const uniqueModels = Array.from(new Set(models.filter(Boolean)));
  const anchors = Array.from(new Set(anchorIds.filter(Boolean)));
  uniqueModels.forEach((model) => {
    const queryParameters = { clickAnalytics: true, filters: RECOMMEND_VISIBILITY_FILTER };
    if (model === algoliaRecommendationParam.TENDING_ITEMS) {
      const facet = trendingCategory
        ? { facetName: TRENDING_CATEGORY_FACET, facetValue: trendingCategory }
        : {};
      requests.push({
        indexName,
        model,
        threshold: RECOMMEND_THRESHOLD,
        maxRecommendations,
        queryParameters,
        ...facet,
      } as RecommendationsRequest);
      meta.push({ model });
      return;
    }
    anchors.forEach((anchor) => {
      requests.push({
        indexName,
        model,
        objectID: anchor,
        threshold: RECOMMEND_THRESHOLD,
        maxRecommendations,
        queryParameters,
      } as RecommendationsRequest);
      meta.push({ model, anchor });
    });
  });
  return { requests, meta };
};

export const isAnchorProduct = (hit: RecommendHitLike, anchorIds: Set<string>): boolean => {
  if (hit?.objectID && anchorIds.has(hit.objectID)) return true;
  return (hit?.variantColorMappings || []).some(
    (variant) => !!variant?.variantId && anchorIds.has(variant.variantId),
  );
};

export const interleave = <T>(lists: T[][]): T[] => {
  const merged: T[] = [];
  const longest = Math.max(0, ...lists.map((list) => list.length));
  for (let position = 0; position < longest; position++) {
    lists.forEach((list) => {
      if (position < list.length) merged.push(list[position]);
    });
  }
  return merged;
};

export const groupRecommendationsByModel = <T extends RecommendHitLike>(
  meta: RecommendRequestMeta[],
  results: Array<{ hits?: T[]; queryID?: string } | undefined>,
  anchorIds: string[],
  indexName: string,
): Record<string, T[]> => {
  const anchors = new Set(anchorIds.filter(Boolean));
  const listsByModel: Record<string, T[][]> = {};
  meta.forEach(({ model }, position) => {
    const result = results[position];
    const hits = (result?.hits || [])
      .filter((hit) => !isAnchorProduct(hit, anchors))
      .map((hit) => ({ ...hit, queryID: result?.queryID ?? "", indexName }));
    (listsByModel[model] = listsByModel[model] || []).push(hits);
  });
  return Object.fromEntries(
    Object.entries(listsByModel).map(([model, lists]) => [model, interleave(lists)]),
  );
};

export const appendCarouselItems = <T extends RecommendHitLike>(
  current: T[],
  incoming: T[],
  limit: number,
  dedupeByParent: boolean,
): T[] => {
  const result = [...current];
  const skus = new Set(result.map((item) => item?.defaultVariant?.sku).filter(Boolean));
  const parents = new Set(result.map((item) => item?.parentProductId).filter(Boolean));
  for (const item of incoming) {
    if (result.length >= limit) break;
    const sku = item?.defaultVariant?.sku;
    if (!sku || skus.has(sku)) continue;
    if (dedupeByParent && item?.parentProductId && parents.has(item.parentProductId)) continue;
    result.push(item);
    skus.add(sku);
    if (item?.parentProductId) parents.add(item.parentProductId);
  }
  return result;
};

export interface CarouselEntry<T> {
  manualItems?: T[];
  model?: string;
}

export const assembleCarousel = <T extends RecommendHitLike>(
  entries: CarouselEntry<T>[],
  recommendedByModel: Record<string, T[]>,
  limit: number,
): T[] => {
  let carousel: T[] = [];
  entries.forEach((entry, position) => {
    if (entry.manualItems) {
      carousel = appendCarouselItems(carousel, entry.manualItems, limit, false);
      return;
    }
    const reservedForLaterManual = entries
      .slice(position + 1)
      .reduce((sum, later) => sum + (later.manualItems?.length ?? 0), 0);
    const autoLimit = Math.max(carousel.length, limit - reservedForLaterManual);
    carousel = appendCarouselItems(
      carousel,
      (entry.model && recommendedByModel[entry.model]) || [],
      autoLimit,
      true,
    );
  });
  return carousel;
};

// Hand-picked sources are looked up by SKU and keep the order set in Sanity.
const SKU_SOURCES = [
  recommendationType.CUSTOM,
  recommendationType.CUSTOM_BASE,
  recommendationType.CUSTOM_INTERMEDIATE,
];

export interface RecommendationSourceLike {
  type?: string;
  products?: Array<{ sku?: string }>;
  collections?: Array<{ slug?: string }>;
}

export const isSkuSource = (source?: RecommendationSourceLike): boolean =>
  !!source?.type && SKU_SOURCES.includes(source.type);

/**
 * The SKUs or collection slugs a hand-picked source asks for, or null when the source is model-based
 * (related, similar, trending, bought together). Collections come from `collections[]`, not `products[]`.
 */
export const getManualSourceValues = (source?: RecommendationSourceLike): string[] | null => {
  if (isSkuSource(source)) {
    return (source?.products || []).map((product) => product?.sku).filter((sku): sku is string => !!sku);
  }
  if (source?.type === recommendationType.SALEOR_COLLECTION) {
    return (source?.collections || [])
      .map((collection) => collection?.slug)
      .filter((slug): slug is string => !!slug);
  }
  return null;
};
